import { useEffect, useRef, useState } from 'react';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const NUMBER = /-?\d+(?:\.\d+)?/g;

// Splits "12 Wins | 76.5% Win Rate" into text parts and numbers (keeping each number's decimals).
const parse = (value) => {
  const text = String(value ?? '');
  const parts = text.split(NUMBER);
  const numbers = (text.match(NUMBER) || []).map((n) => ({
    value: parseFloat(n),
    decimals: n.split('.')[1]?.length || 0,
  }));
  return { parts, numbers };
};

/**
 * Counts every number in a value up from zero (or from its previous values) with an
 * ease-out curve, e.g. 43 → "43", "54.4%" → "54.4%", "12 Wins | 76.5% Win Rate".
 * Renders the final value straight away when the user prefers reduced motion.
 */
export default function CountUp({ value, delay = 0, duration = 900 }) {
  const { parts, numbers } = parse(value);
  const targets = numbers.map((n) => n.value);
  const key = targets.join('|');
  const [current, setCurrent] = useState(() =>
    prefersReducedMotion() ? targets : targets.map(() => 0)
  );
  const fromRef = useRef(current);

  useEffect(() => {
    if (!targets.length) return undefined;
    if (prefersReducedMotion()) {
      setCurrent(targets);
      return undefined;
    }
    const from = fromRef.current.length === targets.length ? fromRef.current : targets.map(() => 0);
    let frame;
    let start = null;
    const tick = (now) => {
      if (start === null) start = now + delay;
      const progress = Math.min(Math.max((now - start) / duration, 0), 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const next = targets.map((to, i) => from[i] + (to - from[i]) * eased);
      fromRef.current = next;
      setCurrent(next);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
    // Re-run only when the target numbers change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, delay, duration]);

  if (!numbers.length) return value;
  return parts
    .map((part, i) =>
      i < numbers.length ? part + (current[i] ?? numbers[i].value).toFixed(numbers[i].decimals) : part
    )
    .join('');
}
